import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Select,
  Stack,
  Text,
  Group,
  Box,
  Button,
  ComboboxItem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { Extension } from "@tiptap/core";

import { useGrossCodes } from "../../hooks/useReferenceData";
import {
  extractVariables,
  replaceSystemVariables,
  stripHtml,
  normalizeLegacyTemplate,
  SYSTEM_VARIABLES,
  type TemplateContext,
} from "../../utils/grossTemplate";
import VariableChip, {
  normalizeVariableName,
  wrapVariablesAsChips,
  type VariableConfig,
} from "../shared/tiptap/VariableChip";
import TemplateChangeDialog from "./TemplateChangeDialog";
import SaveAsTemplateModal from "./SaveAsTemplateModal";
import {
  getFragmentCountDisplayValue,
  getGrossCodeFragmentCountPatch,
  normalizeFragmentChipValue,
  shouldSynchronizeFragmentChip,
  type FragmentCountPatch,
  type FragmentCountQualifier,
} from "../../utils/fragmentCount";

export interface GrossCodeOption {
  value: string;
  label: string;
  description?: string;
  scenarioDescription?: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
}

interface GrossCodeOptionItemProps {
  option: ComboboxItem;
  grossCodes: GrossCodeOption[];
}

function GrossCodeOptionItem({ option, grossCodes }: GrossCodeOptionItemProps) {
  const code = grossCodes.find((c) => c.value === option.value);
  const scenarioDescription = (code as any)?.scenarioDescription || "";
  const truncated =
    scenarioDescription.length > 100
      ? `${scenarioDescription.substring(0, 100)}...`
      : scenarioDescription;

  return (
    <Stack gap={2}>
      <Text size="sm" fw={500}>
        {option.label}
      </Text>
      {truncated && (
        <Text size="xs" c="dimmed" lineClamp={2}>
          {truncated}
        </Text>
      )}
    </Stack>
  );
}

export interface GrossTemplateSelectorHandle {
  focus: () => void;
  // Force-reads the editor's current HTML and propagates it via onChange
  // if it differs from the last value already pushed up. Returns the latest
  // HTML (or null if the editor isn't mounted). Callers should use the
  // returned value directly rather than relying on parent state, since
  // setState from onChange won't have committed synchronously.
  flushPendingContent: () => string | null;
}

const sanitizeOptions = <T extends { value: string | null | undefined }>(
  opts: T[] | undefined
): T[] => {
  if (!Array.isArray(opts)) return [];
  return opts.filter(
    (o) => typeof o.value !== "undefined" && o.value !== null && o.value !== ""
  );
};

interface GrossTemplateSelectorProps {
  value: string;
  onChange: (value: string) => void;
  grossCodeId?: string | null;
  onGrossCodeChange?: (id: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  grossCodes?: GrossCodeOption[];
  specimenLetter?: string;
  specimenCount?: number;
  sourceName?: string;
  subsiteName?: string;
  collectionMethod?: string;
  sizeCm?: string;
  cassetteCount?: number;
  blockCount?: number;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  onFragmentCountChange?: (patch: FragmentCountPatch) => void;
  onTabOut?: () => void;
}

interface PendingTemplate {
  id: string | null;
  text: string;
}

export function collectSavedVariableValues(
  currentHtml: string
): Map<string, string[]> {
  const savedValues = new Map<string, string[]>();
  const valueExtractor =
    /<span([^>]*data-variable="([^"]*)"[^>]*)>.*?<\/span>/gi;

  Array.from(currentHtml.matchAll(valueExtractor)).forEach((match) => {
    const attrs = match[1];
    const name = normalizeVariableName(match[2]);
    const valMatch = attrs.match(/data-variable-value="([^"]*)"/);
    if (!name || !valMatch?.[1]) return;

    if (!savedValues.has(name)) savedValues.set(name, []);
    savedValues.get(name)!.push(valMatch[1]);
  });

  return savedValues;
}

export function enrichTemplateWithSavedVariableValues(
  processedTemplate: string,
  currentHtml: string
): string {
  const savedValues = collectSavedVariableValues(currentHtml);
  if (savedValues.size === 0) return processedTemplate;

  const usedIndices = new Map<string, number>();
  return processedTemplate.replace(
    /\{\{(\w+)\}\}/g,
    (fullMatch, rawName: string) => {
      const name = normalizeVariableName(rawName);
      const values = savedValues.get(name);
      const idx: number = usedIndices.get(name) ?? 0;
      usedIndices.set(name, idx + 1);
      const val = values?.[idx];
      if (!name || !val) return fullMatch;

      return `<span data-variable="${name}" data-variable-value="${val}">{{${name}}}</span>`;
    }
  );
}

const SYSTEM_VAR_NAMES = new Set(Object.keys(SYSTEM_VARIABLES));
const FRAGMENT_VAR_NAMES = new Set(["fragments", "fragment_count"]);
const SITE_VAR_NAMES = new Set([
  "source",
  "subsite",
  "bodysite",
  "location",
  "site",
  "anatomical_site",
]);

const GrossTemplateSelector = forwardRef<
  GrossTemplateSelectorHandle,
  GrossTemplateSelectorProps
>(function GrossTemplateSelector(
  {
    value,
    onChange,
    grossCodeId,
    onGrossCodeChange,
    disabled = false,
    error = false,
    grossCodes: externalGrossCodes,
    specimenLetter,
    specimenCount,
    sourceName,
    subsiteName,
    collectionMethod,
    sizeCm,
    cassetteCount,
    blockCount,
    fragmentCount,
    fragmentCountQualifier,
    onFragmentCountChange,
    onTabOut,
  }: GrossTemplateSelectorProps,
  ref
) {
  const { data: internalGrossCodes = [] } = useGrossCodes();
  const grossCodes = externalGrossCodes || internalGrossCodes;
  const [appliedTemplate, setAppliedTemplate] = useState<string | null>(null);
  const [hasUserTypedManually, setHasUserTypedManually] = useState(false);
  const [rawTemplate, setRawTemplate] = useState<string | null>(null);

  const [dialogOpened, { open: openDialog, close: closeDialog }] =
    useDisclosure(false);
  const [pendingTemplate, setPendingTemplate] =
    useState<PendingTemplate | null>(null);

  const [
    saveTemplateOpened,
    { open: openSaveTemplate, close: closeSaveTemplate },
  ] = useDisclosure(false);

  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onTabOutRef = useRef(onTabOut);
  const templateSelectRef = useRef<HTMLInputElement>(null);
  const dropdownOpenRef = useRef(false);
  const lastStructuredFragmentValueRef = useRef<string | null | undefined>(
    undefined
  );
  onChangeRef.current = onChange;
  onTabOutRef.current = onTabOut;

  const templateBodyContext: TemplateContext = useMemo(
    () => ({
      specimenLetter,
      specimenCount,
      sourceName,
      subsiteName,
      collectionMethod,
      sizeCm,
      cassetteCount,
      blockCount,
    }),
    [
      specimenLetter,
      specimenCount,
      sourceName,
      subsiteName,
      collectionMethod,
      sizeCm,
      cassetteCount,
      blockCount,
    ]
  );

  const templateContext: TemplateContext = useMemo(
    () => ({
      ...templateBodyContext,
      fragmentCount: fragmentCount ?? undefined,
    }),
    [fragmentCount, templateBodyContext]
  );

  const buildVariableConfig = useCallback(
    (html: string = value): VariableConfig => {
      const vars = extractVariables(html);
      const config: VariableConfig = {};
      vars.forEach((v) => {
        const isSiteVar = SITE_VAR_NAMES.has(v);
        const isSystemVar = SYSTEM_VAR_NAMES.has(v);

        if (FRAGMENT_VAR_NAMES.has(v)) {
          // Fragment count: editable chip synced with its structured field.
          const displayValue = getFragmentCountDisplayValue(
            fragmentCount,
            fragmentCountQualifier
          );
          config[v] = {
            options: [],
            value: displayValue || null,
            onChange: (val: string) => {
              // Inline edits are explicit user actions, so normalize them
              // independently of the current structured value.
              const patch = normalizeFragmentChipValue(val, null, null);
              if (
                patch &&
                (patch.fragmentCount !== fragmentCount ||
                  patch.fragmentCountQualifier !== fragmentCountQualifier)
              ) {
                onFragmentCountChange?.(patch);
              }
            },
            readOnly: false,
          };
        } else if (isSiteVar || isSystemVar) {
          const sysVar = SYSTEM_VARIABLES[v];
          let contextKey = sysVar?.contextKey;

          // Fallback context mapping for site variables not explicitly in SYSTEM_VARIABLES
          if (!contextKey && isSiteVar) {
            if (v === "site" || v === "anatomical_site") {
              contextKey = "sourceName";
            }
          }

          const ctxVal = contextKey
            ? templateContext[contextKey as keyof TemplateContext]
            : undefined;
          const hasValue = ctxVal != null && String(ctxVal).trim() !== "";

          config[v] = {
            options: [],
            value: hasValue ? String(ctxVal) : null,
            onChange: () => {},
            readOnly: true,
          };
        } else {
          config[v] = {
            options: [],
            value: null,
            onChange: () => {},
          };
        }
      });
      return config;
    },
    [
      value,
      templateContext,
      fragmentCount,
      fragmentCountQualifier,
      onFragmentCountChange,
    ]
  );
  const buildVariableConfigRef = useRef(buildVariableConfig);
  buildVariableConfigRef.current = buildVariableConfig;

  // Extension that forwards Tab out of the editor to the Save button,
  // unless the cursor is inside a list (where Tab = indent).
  const TabOutExtension = useMemo(
    () =>
      Extension.create({
        name: "tabOut",
        addKeyboardShortcuts() {
          return {
            Tab: ({ editor: e }) => {
              if (e.isActive("bulletList") || e.isActive("orderedList")) {
                return false; // let TipTap handle list indent
              }
              if (onTabOutRef.current) {
                onTabOutRef.current();
                return true; // consumed
              }
              return false;
            },
          };
        },
      }),
    []
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Enter gross description or select a template...",
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      VariableChip,
      TabOutExtension,
    ],
    content: wrapVariablesAsChips(value),
    editable: !disabled,
    autofocus: "end",
    onUpdate: ({ editor: updateEditor }) => {
      if (isUpdatingRef.current) return;
      const html = updateEditor.getHTML();
      if (html !== lastValueRef.current) {
        lastValueRef.current = html;
        onChangeRef.current(html);
        setAppliedTemplate(null);
        setRawTemplate(null);
        if (stripHtml(html).trim() !== "") {
          setHasUserTypedManually(true);
        }
      }
    },
  });

  // Sync variable chip config into editor storage AND update fragment chip node
  // attrs directly. Both must happen in the same effect to avoid state races —
  // dispatching an empty transaction in one effect invalidates the editor state
  // that a second effect would read.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // 1. Write config to storage so chip onChange handlers work
    const cfg = buildVariableConfig();
    (editor.storage as any).variableChip.config = cfg;

    // 2. Update fragment chip node attributes directly via ProseMirror.
    //    Writing to editor.storage alone doesn't trigger React re-renders
    //    in NodeViews, so we must update actual node attrs.
    const newVal =
      getFragmentCountDisplayValue(fragmentCount, fragmentCountQualifier) ||
      null;
    const shouldSyncFragmentValue = shouldSynchronizeFragmentChip(
      lastStructuredFragmentValueRef.current,
      newVal
    );
    lastStructuredFragmentValueRef.current = newVal;
    const { tr } = editor.state;
    let modified = false;

    editor.state.doc.descendants((node, pos) => {
      if (
        shouldSyncFragmentValue &&
        node.type.name === "variableChip" &&
        FRAGMENT_VAR_NAMES.has(node.attrs.name) &&
        node.attrs.value !== newVal
      ) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          value: newVal,
        });
        modified = true;
      }
    });

    if (modified) {
      isUpdatingRef.current = true;
      editor.view.dispatch(tr);
      const html = editor.getHTML();
      lastValueRef.current = html;
      onChangeRef.current(html);
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    } else {
      // No fragment changes — still dispatch empty transaction so other
      // chips (source, subsite, etc.) pick up config from storage
      editor.view.dispatch(editor.state.tr);
    }
  }, [editor, buildVariableConfig, fragmentCount, fragmentCountQualifier]);

  useEffect(() => {
    if (!editor) return;
    const normalizedValue = stripHtml(value || "").trim();
    const normalizedEditorContent = stripHtml(editor.getHTML()).trim();

    if (normalizedValue !== normalizedEditorContent) {
      isUpdatingRef.current = true;
      lastValueRef.current = value;
      (editor.storage as any).variableChip.config =
        buildVariableConfigRef.current(value || "");
      editor.commands.setContent(wrapVariablesAsChips(value || ""));
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  // Re-apply template when context changes (e.g., subsite, source, collection method)
  useEffect(() => {
    if (!rawTemplate) return;
    const processedTemplate = replaceSystemVariables(
      rawTemplate,
      templateBodyContext
    );

    // Preserve user-typed variable values from the current editor before rebuilding.
    const enrichedTemplate =
      editor && !editor.isDestroyed
        ? enrichTemplateWithSavedVariableValues(
            processedTemplate,
            editor.getHTML()
          )
        : processedTemplate;

    const wrapped = wrapVariablesAsChips(enrichedTemplate);
    if (wrapped !== lastValueRef.current) {
      isUpdatingRef.current = true;
      lastValueRef.current = wrapped;
      onChangeRef.current(wrapped);
      if (editor) {
        (editor.storage as any).variableChip.config =
          buildVariableConfigRef.current(wrapped);
        editor.commands.setContent(wrapped);
      }
      setAppliedTemplate(wrapped);
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [templateBodyContext, rawTemplate, editor]);

  const applyTemplateWithContext = useCallback(
    (templateText: string): string => {
      return replaceSystemVariables(templateText, templateBodyContext);
    },
    [templateBodyContext]
  );

  const focusFirstChipOrEditor = useCallback(
    (delay = 50) => {
      setTimeout(() => {
        if (!editor || editor.isDestroyed) return;
        // Force NodeView re-render so chips pick up the new config
        editor.view.dispatch(editor.state.tr);
        // Focus first unfilled chip input if present, else focus editor start
        setTimeout(() => {
          const firstInput =
            editor.view.dom.querySelector<HTMLInputElement>(
              'input[type="text"]'
            );
          if (firstInput) {
            firstInput.focus();
          } else {
            editor.commands.focus("start");
          }
        }, 50);
      }, delay);
    },
    [editor]
  );

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        setTimeout(() => templateSelectRef.current?.focus(), 100);
      },
      flushPendingContent: () => {
        if (!editor || editor.isDestroyed) return null;
        const html = editor.getHTML();
        if (html !== lastValueRef.current) {
          lastValueRef.current = html;
          onChangeRef.current(html);
        }
        return html;
      },
    }),
    [editor]
  );

  const applyTemplateReplace = useCallback(
    (codeId: string | null, templateText: string) => {
      if (onGrossCodeChange) onGrossCodeChange(codeId);

      if (templateText) {
        const normalizedTemplate = normalizeLegacyTemplate(templateText);
        setRawTemplate(normalizedTemplate);
        const processedTemplate = applyTemplateWithContext(normalizedTemplate);
        const wrapped = wrapVariablesAsChips(processedTemplate);

        isUpdatingRef.current = true;
        lastValueRef.current = wrapped;
        onChangeRef.current(wrapped);
        if (editor) {
          (editor.storage as any).variableChip.config =
            buildVariableConfigRef.current(wrapped);
          editor.commands.setContent(wrapped);
        }
        setAppliedTemplate(wrapped);
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
        focusFirstChipOrEditor();
      } else {
        setRawTemplate(null);
        setAppliedTemplate(null);
      }
    },
    [
      onGrossCodeChange,
      applyTemplateWithContext,
      editor,
      focusFirstChipOrEditor,
    ]
  );

  // Auto-apply template when grossCodeId is pre-set externally (e.g. AI extraction)
  useEffect(() => {
    if (!grossCodeId) return;
    if (rawTemplate) return;
    if (stripHtml(value).trim() !== "") return;
    if (grossCodes.length === 0) return;

    const code = grossCodes.find((c) => c.value === grossCodeId);
    const templateText = code?.description || "";
    if (!templateText) return;

    applyTemplateReplace(grossCodeId, templateText);
  }, [grossCodeId, grossCodes, value, rawTemplate, applyTemplateReplace]);

  const applyTemplateAppend = useCallback(
    (codeId: string | null, templateText: string) => {
      const currentHtml =
        editor && !editor.isDestroyed ? editor.getHTML() : value;
      if (onGrossCodeChange) onGrossCodeChange(codeId);

      if (templateText) {
        const normalizedTemplate = normalizeLegacyTemplate(templateText);
        setRawTemplate(null);
        const processedTemplate = applyTemplateWithContext(normalizedTemplate);
        const currentContent = stripHtml(currentHtml).trim();
        const newValue = currentContent
          ? `${currentHtml}<p></p>${wrapVariablesAsChips(processedTemplate)}`
          : wrapVariablesAsChips(processedTemplate);

        isUpdatingRef.current = true;
        lastValueRef.current = newValue;
        onChangeRef.current(newValue);
        if (editor) {
          (editor.storage as any).variableChip.config =
            buildVariableConfigRef.current(newValue);
          editor.commands.setContent(newValue);
        }
        setAppliedTemplate(newValue);
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
        focusFirstChipOrEditor();
      }
    },
    [
      editor,
      onGrossCodeChange,
      value,
      applyTemplateWithContext,
      focusFirstChipOrEditor,
    ]
  );

  const handleGrossCodeSelect = useCallback(
    (codeId: string | null) => {
      if (!codeId) {
        if (onGrossCodeChange) onGrossCodeChange(null);
        onFragmentCountChange?.({
          fragmentCount: null,
          fragmentCountQualifier: null,
        });
        setAppliedTemplate(null);
        return;
      }

      const code = grossCodes.find((c) => c.value === codeId);
      const newTemplate = code?.description || "";

      const fragmentCountPatch =
        fragmentCount == null && fragmentCountQualifier == null
          ? getGrossCodeFragmentCountPatch(
              code?.fragmentCount,
              code?.fragmentCountQualifier
            )
          : null;
      if (fragmentCountPatch) onFragmentCountChange?.(fragmentCountPatch);

      if (stripHtml(value).trim() === "") {
        applyTemplateReplace(codeId, newTemplate);
        return;
      }

      if (hasUserTypedManually && newTemplate) {
        setPendingTemplate({ id: codeId, text: newTemplate });
        openDialog();
        return;
      }

      if (value === appliedTemplate) {
        applyTemplateReplace(codeId, newTemplate);
        return;
      }

      if (newTemplate) {
        setHasUserTypedManually(true);
        setPendingTemplate({ id: codeId, text: newTemplate });
        openDialog();
      }
    },
    [
      onGrossCodeChange,
      onFragmentCountChange,
      fragmentCount,
      fragmentCountQualifier,
      grossCodes,
      value,
      appliedTemplate,
      hasUserTypedManually,
      openDialog,
      applyTemplateReplace,
    ]
  );

  const handleReplace = useCallback(() => {
    if (pendingTemplate) {
      applyTemplateReplace(pendingTemplate.id, pendingTemplate.text);
      setHasUserTypedManually(false);
    }
    setPendingTemplate(null);
    closeDialog();
    setTimeout(() => editor?.commands.focus(), 100);
  }, [pendingTemplate, applyTemplateReplace, closeDialog, editor]);

  const handleAppend = useCallback(() => {
    if (pendingTemplate) {
      applyTemplateAppend(pendingTemplate.id, pendingTemplate.text);
    }
    setPendingTemplate(null);
    closeDialog();
    setTimeout(() => editor?.commands.focus(), 100);
  }, [pendingTemplate, applyTemplateAppend, closeDialog, editor]);

  const handleCancel = useCallback(() => {
    setPendingTemplate(null);
    closeDialog();
    setTimeout(() => editor?.commands.focus(), 100);
  }, [closeDialog, editor]);

  const renderSelectOption = useCallback(
    (props: { option: ComboboxItem }) => (
      <GrossCodeOptionItem option={props.option} grossCodes={grossCodes} />
    ),
    [grossCodes]
  );

  const handleDropdownOpen = useCallback(() => {
    dropdownOpenRef.current = true;
  }, []);

  const handleDropdownClose = useCallback(() => {
    dropdownOpenRef.current = false;
  }, []);

  const handleTemplateKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Tab") return;

      // When description is empty, exact match takes priority
      if (stripHtml(value).trim() === "") {
        const inputValue = (e.target as HTMLInputElement).value
          .trim()
          .toUpperCase();
        if (inputValue) {
          const exactMatch = grossCodes.find(
            (c) =>
              c.value.toUpperCase() === inputValue ||
              c.label.toUpperCase() === inputValue
          );
          if (exactMatch) {
            e.preventDefault();
            handleGrossCodeSelect(exactMatch.value);
            setTimeout(() => {
              if (!editor || editor.isDestroyed) return;
              const firstInput =
                editor.view.dom.querySelector<HTMLInputElement>(
                  'input[type="text"]'
                );
              if (firstInput) {
                firstInput.focus();
              } else {
                editor.commands.focus("start");
              }
            }, 150);
            return;
          }
        }
      }

      // If dropdown is open, always intercept Tab and move to editor
      if (dropdownOpenRef.current) {
        e.preventDefault();
        const activeOption = document.querySelector<HTMLElement>(
          ".mantine-Select-option[data-combobox-selected]"
        );
        if (activeOption) {
          activeOption.click();
        }
        setTimeout(() => {
          if (!editor || editor.isDestroyed) return;
          const firstInput =
            editor.view.dom.querySelector<HTMLInputElement>(
              'input[type="text"]'
            );
          if (firstInput) {
            firstInput.focus();
          } else {
            editor.commands.focus("start");
          }
        }, 150);
      }
    },
    [editor, grossCodes, handleGrossCodeSelect, value]
  );

  const handleEditorClick = useCallback(() => {
    if (editor && !editor.isFocused) {
      editor.commands.focus("end");
    }
  }, [editor]);

  return (
    <>
      <Box>
        <Text size="sm" fw={500} mb={4}>
          Gross Description
        </Text>
        <RichTextEditor
          editor={editor}
          styles={{
            root: {
              border: error
                ? "1px solid var(--mantine-color-red-6)"
                : "1px solid var(--mantine-color-gray-4)",
              borderRadius: "var(--mantine-radius-sm)",
            },
            content: {
              minHeight: 200,
              backgroundColor: disabled
                ? "var(--mantine-color-gray-1)"
                : "white",
              cursor: "text",
            },
            toolbar: {
              borderBottom: "1px solid var(--mantine-color-gray-3)",
            },
          }}
        >
          <RichTextEditor.Toolbar sticky stickyOffset={60}>
            <Box mr={4} style={{ display: "flex", alignItems: "center" }}>
              <Select
                ref={templateSelectRef}
                placeholder="Template..."
                data={sanitizeOptions(grossCodes)}
                value={grossCodeId || null}
                onChange={handleGrossCodeSelect}
                searchable
                clearable
                disabled={disabled}
                renderOption={renderSelectOption}
                onDropdownOpen={handleDropdownOpen}
                onDropdownClose={handleDropdownClose}
                onKeyDown={handleTemplateKeyDown}
                size="xs"
                styles={{
                  input: {
                    width: 180,
                    fontFamily: "monospace",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Undo />
              <RichTextEditor.Redo />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.ColorPicker
                colors={[
                  "#000000",
                  "#868e96",
                  "#fa5252",
                  "#e64980",
                  "#be4bdb",
                  "#7950f2",
                  "#4c6ef5",
                  "#228be6",
                  "#15aabf",
                  "#12b886",
                  "#40c057",
                  "#82c91e",
                  "#fab005",
                  "#fd7e14",
                ]}
              />
              <RichTextEditor.UnsetColor tabIndex={-1} />
              <RichTextEditor.Highlight tabIndex={-1} />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>

          <RichTextEditor.Content onClick={handleEditorClick} />
        </RichTextEditor>
        {error && (
          <Text size="xs" c="red" mt={4}>
            Gross description is required
          </Text>
        )}
        <Group justify="flex-end" mt={4}>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={openSaveTemplate}
            disabled={disabled || !stripHtml(value).trim()}
            tabIndex={-1}
          >
            Save as Template
          </Button>
        </Group>
      </Box>

      <TemplateChangeDialog
        opened={dialogOpened}
        onClose={handleCancel}
        onReplace={handleReplace}
        onAppend={handleAppend}
        templatePreview={pendingTemplate?.text || ""}
        hasManualText={hasUserTypedManually}
      />

      <SaveAsTemplateModal
        opened={saveTemplateOpened}
        onClose={closeSaveTemplate}
        description={value}
      />
    </>
  );
});

export default GrossTemplateSelector;
